import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { GandalfFormBuilder, GandalfModule, TypedFormGroup } from 'gandalf';
import { ModalBase } from 'majora';
import { CategoryService, CategoryResponseForDropdown } from '@core/category/category.service';
import { TaskPriority, CategoryEntityType } from '@gandalf/constants';
import { CreateTaskRequest } from '@gandalf/model/create-task-request';
import { TaskResponse } from '@gandalf/model/task-response';
import { SharedModule } from '@shared/shared.module';
import { conditionallyRequiredValidator } from '@shared/validators/conditionally-required-validation';
import { TaskService } from '@core/task/task.service';
import { TASK_MASTER_CATEGORY } from '../../task-master-category.constants';
import { TaskFormFieldsComponent } from '../../task-form-fields/task-form-fields.component';

export interface CreateTaskModalData {
	patientId?: number;
	description?: string;
	categoryId?: number;
	priority?: TaskPriority;
	notifyOnCompletion?: boolean;
	notifyOnUpdate?: boolean;
	entityId?: number;
	patientDocumentId?: number;
	disableCategorySelect?: boolean;
	disablePatientSelect?: boolean;
}

@Component({
	selector: 'pms-create-task-modal',
	templateUrl: './create-task-modal.component.html',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		GandalfModule,
		SharedModule,
		TaskFormFieldsComponent,
	],
})
export class CreateTaskModalComponent extends ModalBase<CreateTaskModalData, TaskResponse> implements OnInit {
	private gandalfFormBuilder = inject(GandalfFormBuilder);
	private taskManagementService = inject(TaskService);
	private categoryService = inject(CategoryService);

	componentForm: TypedFormGroup<CreateTaskRequest>;
	request: CreateTaskRequest;
	categories: CategoryResponseForDropdown[] = [];
	disableSave = false;
	isLoading = true;

	ngOnInit() {
		const createRequest = new CreateTaskRequest();
		createRequest.assignToAll = false;
		createRequest.patientId = this.data?.patientId;
		createRequest.description = this.data?.description;
		createRequest.categoryId = this.data?.categoryId;
		createRequest.priority = this.data?.priority ?? TaskPriority.NORMAL;
		createRequest.notifyOnCompletion = this.data?.notifyOnCompletion ?? false;
		createRequest.notifyOnUpdate = this.data?.notifyOnUpdate ?? false;
		createRequest.entityId = this.data?.entityId;
		createRequest.patientDocumentId = this.data?.patientDocumentId;
		this.request = createRequest;
		this.componentForm = this.gandalfFormBuilder.groupTyped(createRequest, {
			validators: [
				conditionallyRequiredValidator(
					'assigneePersonId',
					(form) => !(form.get('optionalAssigneePersonIds')?.value?.length > 0),
					'required',
					'Assigned To is required',
				),
			],
		});

		if (this.data?.disableCategorySelect) {
			this.componentForm.controls.categoryId.disable();
		}
		if (this.data?.disablePatientSelect) {
			this.componentForm.controls.patientId.disable();
		}

		this.categoryService.findActiveByEntityType(CategoryEntityType.TASK_ITEM)
			.subscribe(categories => {
				this.categories = this.data?.patientDocumentId
					? categories
					: categories.filter(category => category.masterId !== TASK_MASTER_CATEGORY.LETTER_DRAFT);
				this.isLoading = false;
			});
	}

	save() {
		if (this.componentForm.invalid || this.disableSave) {
			return;
		}
		this.disableSave = true;

		const createRequest = this.componentForm.getRawValue() as CreateTaskRequest;
		this.taskManagementService.createTask(createRequest).pipe(
			finalize(() => this.disableSave = false),
		).subscribe((response) => {
			this.close(response);
		});
	}
}
